import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import RefreshIcon from "@material-ui/icons/Refresh";
import SaveIcon from "@material-ui/icons/Save";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  heroCard: {
    padding: theme.spacing(2),
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    background: "linear-gradient(180deg, rgba(25,118,210,0.06) 0%, rgba(25,118,210,0.01) 100%)",
    marginBottom: theme.spacing(2),
  },
  columnCard: {
    padding: theme.spacing(2),
    height: "100%",
    borderRadius: 14,
    background: theme.palette.background.paper,
  },
  sectionCard: {
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 10,
    background: theme.palette.background.default,
  },
  sectionTitle: {
    fontWeight: 700,
    marginBottom: theme.spacing(0.5),
  },
  sectionText: {
    color: theme.palette.text.secondary,
    whiteSpace: "pre-wrap",
  },
  goalText: {
    marginTop: theme.spacing(0.75),
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
    fontStyle: "italic",
    whiteSpace: "pre-wrap",
  },
  metricRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.75),
    marginTop: theme.spacing(1),
  },
  metricChip: {
    background: theme.palette.background.paper,
  },
  blockNoteField: {
    marginTop: theme.spacing(1.25),
  },
  blockExamples: {
    marginTop: theme.spacing(1.25),
  },
  flowChipRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    margin: theme.spacing(1.5, 0),
  },
  subtleNote: {
    color: theme.palette.text.secondary,
  },
  notesCard: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 14,
  },
  footerActions: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    marginTop: theme.spacing(2),
  },
  footerButtons: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  statusRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  topRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: theme.spacing(2),
    flexWrap: "wrap",
  },
}));

const safeText = (value, fallback = "-") => {
  const text = String(value || "").trim();
  return text || fallback;
};

const formatDate = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleString("pt-BR");
};

const formatRiskLabel = (value) => {
  const risk = String(value || "").trim().toLowerCase();
  if (risk === "alta") return "Alto";
  if (risk === "media" || risk === "média") return "Médio";
  if (risk === "baixa" || risk === "baixo") return "Baixo";
  return safeText(value, "-");
};

const formatConfidenceLabel = (value, score) => {
  const label = String(value || "").trim();
  if (label) return label;
  if (score >= 90) return "Alta";
  if (score >= 75) return "Média";
  if (score > 0) return "Baixa";
  return "-";
};

const formatImpactLabel = (value) => safeText(value, "-");

const cloneFlowStep = (step, fallbackIndex = 0) => ({
  id: String(step?.id || `step_${fallbackIndex + 1}`),
  title: String(step?.title || "").trim(),
  current: String(step?.current || "").trim(),
  proposed: String(step?.proposed || "").trim(),
  sourceCount: Number(step?.sourceCount || 0),
  sourceCategories: Array.isArray(step?.sourceCategories) ? step.sourceCategories : [],
  confidenceScore: step?.confidenceScore != null ? Number(step.confidenceScore) : 0,
  confidenceLabel: String(step?.confidenceLabel || "").trim(),
  riskLevel: String(step?.riskLevel || "").trim(),
  impactLevel: String(step?.impactLevel || "").trim(),
  agentCount: step?.agentCount != null ? Number(step.agentCount) : 0,
  exampleCount: step?.exampleCount != null ? Number(step.exampleCount) : 0,
  notes: String(step?.notes || "").trim()
});

const normalizeFlowDraft = (flow) =>
  (Array.isArray(flow) ? flow : []).map((step, index) => cloneFlowStep(step, index));

const LyraEvolution = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);

  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState("");
  const [proposal, setProposal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [implementing, setImplementing] = useState(false);
  const [notes, setNotes] = useState("");
  const [finalProposalFlow, setFinalProposalFlow] = useState([]);

  const isAdmin = user?.profile === "admin";

  const fetchUsers = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const { data } = await api.get("/users/list");
      setUsers(data || []);
    } catch (err) {
      toastError(err);
    }
  }, [isAdmin]);

  const fetchProposal = useCallback(
    async (userIdParam = selectedUser) => {
      setLoading(true);
      try {
        const { data } = await api.get("/lyra/evolution/proposal", {
          params: userIdParam ? { userId: userIdParam } : undefined
        });
        setProposal(data || null);
        const savedFlow =
          Array.isArray(data?.finalProposalFlow) && data.finalProposalFlow.length > 0
            ? data.finalProposalFlow
            : data?.proposedFlow || [];
        setFinalProposalFlow(normalizeFlowDraft(savedFlow));
        setNotes(data?.finalProposalNotes || data?.savedProposal?.notes || "");
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    },
    [selectedUser]
  );

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    fetchProposal().catch(() => undefined);
  }, [fetchProposal]);

  const scopeLabel = useMemo(() => {
    if (proposal?.scope?.type === "usuario") {
      return `Usuário: ${proposal?.scope?.userName || "selecionado"}`;
    }
    return proposal?.scope?.companyId === 5 ? "Empresa 5" : "Escopo: empresa inteira";
  }, [proposal]);

  const generatedFlow = proposal?.proposedFlow || [];
  const flowToEdit = finalProposalFlow.length > 0 ? finalProposalFlow : normalizeFlowDraft(generatedFlow);
  const canEditProposal = isAdmin;
  const hasCustomFlow = Boolean(proposal?.finalProposalMeta?.hasCustomFlow);

  const updateFlowStep = (index, field, value) => {
    setFinalProposalFlow((current) =>
      current.map((item, itemIndex) =>
        itemIndex === index
          ? {
              ...item,
              [field]: value
            }
          : item
      )
    );
  };

  const handleSaveDraft = async () => {
    setSavingDraft(true);
    try {
      const { data } = await api.post("/lyra/evolution/proposal/draft", {
        userId: selectedUser || undefined,
        notes,
        finalProposalFlow: flowToEdit
      });
      toast.success("Rascunho salvo com sucesso.");
      setProposal(data?.generatedProposal || proposal);
      await fetchProposal(selectedUser);
    } catch (err) {
      toastError(err);
    } finally {
      setSavingDraft(false);
    }
  };

  const handleImplement = async () => {
    setImplementing(true);
    try {
      const { data } = await api.post("/lyra/evolution/proposal/implement", {
        userId: selectedUser || undefined,
        notes,
        finalProposalFlow: flowToEdit
      });
      toast.success("Proposta aprovada e implantada com sucesso.");
      setProposal(data?.generatedProposal || proposal);
      await fetchProposal(selectedUser);
    } catch (err) {
      toastError(err);
    } finally {
      setImplementing(false);
    }
  };

  const currentFlow = useMemo(() => proposal?.currentFlow || [], [proposal?.currentFlow]);
  const categories = useMemo(() => proposal?.categorySummary || [], [proposal?.categorySummary]);
  const highlights = useMemo(() => proposal?.approvedHighlights || [], [proposal?.approvedHighlights]);
  const categoryMap = useMemo(
    () => new Map(categories.map((item) => [item.category, item])),
    [categories]
  );

  return (
    <MainContainer>
      <MainHeader>
        <Title>Evolução LYRA</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<ArrowBackIcon />}
            onClick={() => history.push("/lyra-learning")}
          >
            Voltar ao aprendizado
          </Button>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={() => fetchProposal(selectedUser)}
            disabled={loading}
          >
            Atualizar comparação
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper}>
        <Paper variant="outlined" className={classes.heroCard}>
          <Box className={classes.topRow}>
            <Box>
              <Typography variant="h5">Painel executivo da LYRA</Typography>
              <Typography variant="body2" className={classes.subtleNote}>
                Aqui você compara o fluxo atual com a proposta gerada pelos aprendizados aprovados e decide se salva como rascunho ou se implanta.
              </Typography>
              <Typography variant="caption" className={classes.subtleNote}>
                {scopeLabel}
              </Typography>
            </Box>
            {isAdmin && (
              <FormControl variant="outlined" size="small" style={{ minWidth: 280 }}>
                <InputLabel>Escopo</InputLabel>
                <Select
                  value={selectedUser}
                  onChange={(e) => {
                    setSelectedUser(e.target.value);
                  }}
                  label="Escopo"
                >
                  <MenuItem value="">Empresa inteira</MenuItem>
                  {users.map((item) => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>

          <Box className={classes.statusRow}>
            <Chip size="small" color="primary" label={`${proposal?.summary?.totalApproved || 0} aprovados`} />
            <Chip size="small" variant="outlined" label={`${proposal?.summary?.categories || 0} categorias`} />
            <Chip size="small" variant="outlined" label={`${proposal?.summary?.flowSteps || 0} blocos detalhados`} />
            <Chip size="small" variant="outlined" label={proposal?.savedProposal?.status === "implemented" ? "Já implantada" : proposal?.savedProposal?.status === "draft" ? "Rascunho salvo" : "Sem rascunho salvo"} />
            <Chip size="small" variant="outlined" label={hasCustomFlow ? "Versão final editada" : "Versão gerada"} />
            {proposal?.savedProposal?.payload?.generatedProposal?.refreshedFromLearning ? (
              <Chip size="small" variant="outlined" label="Atualizado após requalificação" />
            ) : null}
            <Chip size="small" variant="outlined" label={`Atualizado em ${formatDate(proposal?.savedProposal?.updatedAt || proposal?.generatedAt)}`} />
          </Box>
        </Paper>

        {loading && !proposal ? (
          <Paper variant="outlined" className={classes.notesCard}>
            <Typography variant="body2" className={classes.subtleNote}>
              Montando a comparação da LYRA...
            </Typography>
          </Paper>
        ) : proposal?.ok ? (
          <>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" className={classes.columnCard}>
                  <Typography variant="h6" color="primary">
                    Como está hoje
                  </Typography>
                  <Typography variant="body2" className={classes.subtleNote}>
                    Fluxo que o sistema já executa agora.
                  </Typography>
                  <Box mt={2}>
                    {currentFlow.map((item) => (
                      <Box key={item.id} className={classes.sectionCard}>
                        <Typography className={classes.sectionTitle}>{item.title}</Typography>
                        <Typography className={classes.sectionText}>{safeText(item.current)}</Typography>
                        {item.goal ? (
                          <Typography className={classes.goalText}>Objetivo operacional: {safeText(item.goal)}</Typography>
                        ) : null}
                      </Box>
                    ))}
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper variant="outlined" className={classes.columnCard}>
                  <Typography variant="h6" color="primary">
                    Como pode ficar
                  </Typography>
                  <Typography variant="body2" className={classes.subtleNote}>
                    Versão final editável pelo admin antes da aplicação, com métricas executivas e observações por bloco.
                  </Typography>
                  <Box mt={2}>
                    {flowToEdit.map((item, index) => (
                      <Box key={item.id} className={classes.sectionCard}>
                        <Typography className={classes.sectionTitle}>{item.title}</Typography>
                        <Typography className={classes.sectionText}>{safeText(item.current)}</Typography>
                        {canEditProposal ? (
                          <TextField
                            fullWidth
                            multiline
                            rows={4}
                            variant="outlined"
                            label="Texto final da proposta"
                            value={item.proposed}
                            onChange={(event) => updateFlowStep(index, "proposed", event.target.value)}
                            margin="dense"
                          />
                        ) : (
                          <Typography className={classes.sectionText}>{safeText(item.proposed)}</Typography>
                        )}
                        <Box className={classes.metricRow}>
                          {item.sourceCount > 0 && (
                            <Chip
                              size="small"
                              color="primary"
                              className={classes.metricChip}
                              label={`${item.sourceCount} atendimentos usados`}
                            />
                          )}
                          <Chip
                            size="small"
                            variant="outlined"
                            className={classes.metricChip}
                            label={`Confiança: ${formatConfidenceLabel(item.confidenceLabel, item.confidenceScore)}`}
                          />
                          <Chip
                            size="small"
                            variant="outlined"
                            className={classes.metricChip}
                            label={`Risco: ${formatRiskLabel(item.riskLevel)}`}
                          />
                          <Chip
                            size="small"
                            variant="outlined"
                            className={classes.metricChip}
                            label={`Impacto: ${formatImpactLabel(item.impactLevel)}`}
                          />
                          <Chip
                            size="small"
                            variant="outlined"
                            className={classes.metricChip}
                            label={`${item.agentCount || 0} agentes`}
                          />
                          <Chip
                            size="small"
                            variant="outlined"
                            className={classes.metricChip}
                            label={`${item.exampleCount || 0} exemplos`}
                          />
                        </Box>
                        {canEditProposal ? (
                          <TextField
                            fullWidth
                            multiline
                            rows={2}
                            variant="outlined"
                            label="Observação do bloco"
                            value={item.notes || ""}
                            onChange={(event) => updateFlowStep(index, "notes", event.target.value)}
                            margin="dense"
                            className={classes.blockNoteField}
                          />
                        ) : item.notes ? (
                          <Typography className={classes.goalText}>Observação: {safeText(item.notes)}</Typography>
                        ) : null}
                        {categoryMap.get(item.id)?.examples?.length ? (
                          <Box className={classes.blockExamples}>
                            <Typography variant="caption" className={classes.subtleNote}>
                              Exemplos aprovados deste bloco
                            </Typography>
                            <Box mt={0.75}>
                              {categoryMap.get(item.id)?.examples?.slice(0, 2).map((example) => (
                                <Box key={example.id} mb={1}>
                                  <Typography variant="body2" className={classes.sectionTitle}>
                                    {example.title}
                                  </Typography>
                                  <Typography variant="body2" className={classes.sectionText}>
                                    {example.evidence}
                                  </Typography>
                                  <Typography variant="caption" className={classes.subtleNote}>
                                    Sugestão aprovada: {example.proposedChange}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        ) : null}
                      </Box>
                    ))}
                  </Box>
                </Paper>
              </Grid>
            </Grid>

            <Paper variant="outlined" className={classes.notesCard}>
              <Typography variant="h6">Classificação e origem do aprendizado</Typography>
              <Typography variant="body2" className={classes.subtleNote}>
                A proposta executiva usa só conteúdo aprovado. O admin pode salvar como rascunho ou aprovar e implantar.
              </Typography>

              <Box className={classes.flowChipRow}>
                {categories.map((item) => (
                  <Chip
                    key={item.category}
                    size="small"
                    variant="outlined"
                    label={`${item.title} (${item.approvedCount})`}
                  />
                ))}
              </Box>

              <Grid container spacing={2}>
                {highlights.slice(0, 4).map((item) => (
                  <Grid item xs={12} md={6} key={item.id}>
                    <Paper variant="outlined" className={classes.sectionCard}>
                      <Typography className={classes.sectionTitle}>{item.title}</Typography>
                      <Typography className={classes.sectionText}>{item.evidence}</Typography>
                      <Typography variant="caption" className={classes.subtleNote}>
                        Sugestão aprovada: {item.proposedChange}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Paper>

            {canEditProposal && (
              <Paper variant="outlined" className={classes.notesCard}>
                <Typography variant="h6">Notas do admin</Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  variant="outlined"
                  label="Observações e reavaliação"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  margin="normal"
                />

                <Box className={classes.footerActions}>
                  <Typography variant="body2" className={classes.subtleNote}>
                    O admin pode ajustar apenas a versão final da proposta. O fluxo atual permanece como referência factual.
                  </Typography>
                  <Box className={classes.footerButtons}>
                    <Button
                      variant="outlined"
                      color="primary"
                      startIcon={<RefreshIcon />}
                      onClick={() => {
                        setFinalProposalFlow(normalizeFlowDraft(generatedFlow));
                        setNotes("");
                      }}
                      disabled={savingDraft || implementing}
                    >
                      Restaurar proposta
                    </Button>
                    <Button
                      variant="outlined"
                      color="primary"
                      startIcon={<SaveIcon />}
                      onClick={handleSaveDraft}
                      disabled={savingDraft || implementing}
                    >
                      {savingDraft ? "Salvando..." : "Salvar rascunho"}
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<CheckCircleOutlineIcon />}
                      onClick={handleImplement}
                      disabled={savingDraft || implementing}
                    >
                      {implementing ? "Implantando..." : "Aprovar e implantar"}
                    </Button>
                  </Box>
                </Box>
              </Paper>
            )}
          </>
        ) : (
          <Paper variant="outlined" className={classes.notesCard}>
            <Typography variant="body2" className={classes.subtleNote}>
              Ainda não há proposta disponível para este escopo.
            </Typography>
          </Paper>
        )}
      </Paper>
    </MainContainer>
  );
};

export default LyraEvolution;
